import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,

  Checkbox,
  FormControlLabel,
  Alert,
  Divider,
} from '@mui/material';
import { Security as SecurityIcon } from '@mui/icons-material';
import { ROLE_PERMISSIONS, PERMISSION_GROUPS, Permission } from '../contexts/AuthContext';
import { universalModalPaperProps, universalModalBackdropProps } from '../styles/modalStyles';

interface SalesforceUser {
  Id: string;
  Email__c: string;
  First_Name__c: string;
  Last_Name__c: string;
  Role__c: string;
  Is_Active__c: boolean;
  Last_Login__c: string | null;
  Permissions__c?: string;
}

interface PermissionsManagerProps {
  selectedUser?: SalesforceUser;
  initialPermissions?: Permission[];
  onClose?: () => void;
  onSave?: (permissions: Permission[]) => void;
}

const PermissionsManager: React.FC<PermissionsManagerProps> = ({ selectedUser, initialPermissions, onClose, onSave }) => {
  const [open, setOpen] = useState(!!selectedUser);
  const [customPermissions, setCustomPermissions] = useState<Permission[]>([]);
  const [error] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (selectedUser) {
      // For user-specific permissions, load custom permissions or fall back to role permissions
      if (selectedUser.Permissions__c) {
        try {
          setCustomPermissions(JSON.parse(selectedUser.Permissions__c));
        } catch {
          setCustomPermissions(initialPermissions || ROLE_PERMISSIONS[selectedUser.Role__c] || []);
        }
      } else {
        setCustomPermissions(initialPermissions || ROLE_PERMISSIONS[selectedUser.Role__c] || []);
      }
      setOpen(true);
    }
  }, [initialPermissions, selectedUser]);

  const handleClose = () => {
    setOpen(false);
    if (onClose) onClose();
  };

  const handlePermissionToggle = (permission: Permission) => {
    setCustomPermissions(prev => 
      prev.includes(permission) 
        ? prev.filter(p => p !== permission)
        : [...prev, permission]
    );
  };

  const handleGroupToggle = (groupPermissions: Permission[]) => {
    const allIncluded = groupPermissions.every(p => customPermissions.includes(p));
    if (allIncluded) {
      // Remove all permissions in the group
      setCustomPermissions(prev => prev.filter(p => !groupPermissions.includes(p)));
    } else {
      // Add all permissions in the group
      setCustomPermissions(prev => {
        const newPermissions = [...prev, ...groupPermissions];
        return Array.from(new Set(newPermissions));
      });
    }
  };

  const getPermissionDescription = (permission: Permission): string => {
    const descriptions: Record<Permission, string> = {
      'view_prerequisites': 'Can view and check student prerequisites',
      'manage_prerequisites': 'Can edit and manage prerequisite rules',
      'view_attendance': 'Can view student attendance and class lists',
      'manage_attendance': 'Can take attendance and manage student records',
      'view_courses': 'Can view course information and schedules',
      'manage_courses': 'Can edit and manage course data',
      'view_course_builder': 'Can view and access the course builder interface',
      'manage_course_builder': 'Can create and manage classrooms, courses, and sessions',
      'view_temp_cards': 'Can view and generate temporary certification cards',
      'manage_temp_cards': 'Can manage temp card templates and settings',
      'view_test_sheet_submissions': 'Can view test sheet email submissions',
      'manage_test_sheet_submissions': 'Can sync, tag, export, and manage test sheet submissions',
      'view_dob_verifier': 'Can verify student dates of birth',
      'manage_dob_verifier': 'Can update student dates of birth',
      'manage_users': 'Can create, edit, and delete user accounts',
      'admin_access': 'Full administrative access to all features',
    };
    return descriptions[permission] || permission;
  };

  const getGroupStatus = (groupPermissions: Permission[]) => {
    const includedCount = groupPermissions.filter(p => customPermissions.includes(p)).length;
    if (includedCount === 0) return 'none';
    if (includedCount === groupPermissions.length) return 'all';
    return 'partial';
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      BackdropProps={universalModalBackdropProps}
      PaperProps={universalModalPaperProps}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SecurityIcon />
        {selectedUser 
          ? `Custom Permissions for ${selectedUser.First_Name__c} ${selectedUser.Last_Name__c}` 
          : 'Permissions Overview'
        }
      </DialogTitle>
      
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Configure custom permissions for this account. Custom permissions override role defaults and are saved on the Salesforce user record.
        </Typography>

        <Grid container spacing={3}>
          {Object.entries(PERMISSION_GROUPS).map(([groupName, groupPermissions]) => {
            const status = getGroupStatus(groupPermissions);
            return (
              <Grid item xs={12} md={6} key={groupName}>
                <Card 
                  variant="outlined" 
                  sx={{ 
                    height: '100%',
                    border: status === 'all' ? '2px solid' : '1px solid',
                    borderColor: status === 'all' ? 'success.main' : status === 'partial' ? 'warning.main' : 'divider'
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" component="h3">
                        {groupName}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Chip 
                          label={status === 'all' ? 'All' : status === 'partial' ? 'Partial' : 'None'}
                          color={status === 'all' ? 'success' : status === 'partial' ? 'warning' : 'default'}
                          size="small"
                        />
                        <Button
                          size="small"
                          variant={status === 'all' ? 'outlined' : 'contained'}
                          onClick={() => handleGroupToggle(groupPermissions)}
                        >
                          {status === 'all' ? 'Remove All' : 'Add All'}
                        </Button>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {groupPermissions.map((permission) => (
                        <FormControlLabel
                          key={permission}
                          control={
                            <Checkbox
                              checked={customPermissions.includes(permission)}
                              onChange={() => handlePermissionToggle(permission)}
                              size="small"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {permission.replace('_', ' ').toUpperCase()}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {getPermissionDescription(permission)}
                              </Typography>
                            </Box>
                          }
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Summary ({customPermissions.length} permissions selected)
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {customPermissions.map((permission) => (
              <Chip
                key={permission}
                label={permission.replace('_', ' ')}
                size="small"
                color="primary"
                variant="outlined"
                onDelete={() => handlePermissionToggle(permission)}
              />
            ))}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        {selectedUser?.Permissions__c && (
          <Button
            color="warning"
            onClick={() => {
              if (onSave) {
                onSave([]);
              }
              setSuccess('Custom permissions cleared successfully!');
              setTimeout(() => handleClose(), 1000);
            }}
          >
            Clear Custom Permissions
          </Button>
        )}
        <Button onClick={handleClose}>
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={() => {
            if (onSave) {
              onSave(customPermissions);
            }
            setSuccess('Permissions updated successfully!');
            setTimeout(() => handleClose(), 1500);
          }}
        >
          Save Permissions
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PermissionsManager; 
